import { makeStyles, Typography, Paper } from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import React from "react";
import { KitProfileCardFragment } from "@graphql";
import Skeleton from "@material-ui/lab/Skeleton";
import moment from "moment";
import locale from "@bonliva-crm/locale";
import { useNavManager } from "@hooks";
import {
  BOOKING_SLIDER,
  CONSULTANT_SLIDER,
  CUSTOMER_SLIDER,
  ConsultantProfileLocation,
  CustomerProfileLocation,
  BookingProfileLocation
} from "@navigation";
import { toSEK } from "@utils/formatCurrency";

type Props = {
  bookingId: string;
  data: KitProfileCardFragment;
  loading: boolean;
};

export const BookingKitCard = ({ bookingId, data, loading }: Props) => {
  const classes = useStyles();

  const { navManager } = useNavManager();

  const openBooking = () => {
    navManager.open(BOOKING_SLIDER, new BookingProfileLocation(bookingId));
  };
  const openConsultant = (id: string) => {
    navManager.open(CONSULTANT_SLIDER, new ConsultantProfileLocation(id));
  };
  const openCustomer = (id: string) => {
    navManager.open(CUSTOMER_SLIDER, new CustomerProfileLocation(id));
  };

  if (loading) {
    return (
      <div className={classes.skeletonWrap}>
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} width={"70%"} />
      </div>
    );
  }
  if (!data) {
    return <Typography>{`booking not found ${bookingId}`}</Typography>;
  }

  return (
    <Paper className={classes.root} elevation={0} square>
      <div className={classes.row}>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>{locale.Week}</Typography>
          <strong className={classes.link} onClick={openBooking}>
            {`V.${moment(data.week).format("W/YYYY")}`}{" "}
            <OpenInNewIcon color={"inherit"} fontSize={"small"} />{" "}
          </strong>
        </div>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>{"Kst. ställe & Prj"}</Typography>
          <strong>
            {`${data.costCenter?.Name} – ${data.project?.Name} ${data.project?.ProjectNumber}`}
          </strong>
        </div>
      </div>

      <div className={classes.row}>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>{locale.Consultant}</Typography>
          {data.consultant && (
            <strong
              className={classes.link}
              onClick={() => openConsultant(data.consultant._id)}
            >
              {data.consultant.Name}
              <OpenInNewIcon color={"inherit"} fontSize={"small"} />{" "}
            </strong>
          )}
        </div>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>{locale.payoutPeriod}</Typography>
          <strong>{data.consultantPayoutType || "n/a"}</strong>
        </div>
      </div>

      <div className={classes.row}>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>{locale.WorkPlace}</Typography>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {data.hospital && (
              <strong
                className={classes.link}
                onClick={() => openCustomer(data.hospital._id)}
              >
                {data.hospital?.Name}
                <OpenInNewIcon color={"inherit"} fontSize={"small"} />{" "}
              </strong>
            )}
            {data.department && (
              <strong
                className={classes.link}
                onClick={() => openCustomer(data.department._id)}
              >
                {data.department?.Name}
                <OpenInNewIcon color={"inherit"} fontSize={"small"} />{" "}
              </strong>
            )}
          </div>
        </div>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>{"Samlingsfaktura"}</Typography>
          <strong>{data.isGroupInvoicing ? "Ja" : "Nej"}</strong>
        </div>
      </div>

      <div className={classes.row}>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>{locale.billingInstructions}</Typography>
          <strong>{data.customerBillingInstructions || "n/a"}</strong>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>TB1</Typography>
          <strong>{toSEK(data.profitNet) || "n/a"}</strong>
        </div>
        <div className={classes.col}>
          <Typography variant={"subtitle1"}>TG</Typography>
          <strong>{(data.profitRatio || 0) + "%" || "n/a"}</strong>
        </div>
      </div>
    </Paper>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(2, 0)
  },
  skeletonWrap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  link: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    display: "inline-flex",
    alignItems: "center",
    "&:hover": {
      cursor: "pointer"
    }
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& > div:not(:first-child)": {
      textAlign: "right"
    }
  },
  col: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1, 1, 1)
  }
}));
